/**
 * InlineDiff - Word-level inline diff highlighting between two texts.
 *
 * Renders removed words in red (strikethrough) and added words in green.
 * The diff itself lives in `reviewDiff` (shared with the document viewer's
 * change highlighting). `compact` shows only the changes with a few words of
 * context either side - "…not less than ~~30~~ 60 days written notice…" -
 * which is how a reader takes in a one-word edit to a long clause (F-REVIEW).
 */

import React, { useMemo } from 'react';
import { buildDiff, reviewChange, stripLeadingNumbering } from './reviewDiff';

interface InlineDiffProps {
  /** The original text (before modification). */
  original: string;
  /** The modified text (after modification). */
  modified: string;
  /** Only the changed words, with context, instead of the whole text. */
  compact?: boolean;
}

const Removed: React.FC<{ text: string }> = ({ text }) => (
  <span className="bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 line-through">{text}</span>
);
const Added: React.FC<{ text: string }> = ({ text }) => (
  <span className="bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">{text}</span>
);

const InlineDiff: React.FC<InlineDiffProps> = ({ original, modified, compact = false }) => {
  const segments = useMemo(
    () => buildDiff(stripLeadingNumbering(original), stripLeadingNumbering(modified)),
    [original, modified],
  );
  const hunks = useMemo(
    () => (compact ? reviewChange(original, modified)?.hunks || [] : []),
    [compact, original, modified],
  );

  if (compact && hunks.length > 0) {
    return (
      <div className="space-y-1.5 text-sm leading-relaxed" data-testid="inline-diff-compact">
        {hunks.map((h, idx) => (
          <p key={idx} className="text-fg-muted">
            {h.before && <span>…{h.before}</span>}
            {h.deleted.trim() && <Removed text={h.deleted} />}
            {h.inserted.trim() && <Added text={h.inserted} />}
            {h.after && <span>{h.after}…</span>}
          </p>
        ))}
      </div>
    );
  }

  return (
    <div className="text-sm leading-relaxed">
      {segments.map((seg, idx) => {
        if (seg.type === 'removed') return <Removed key={idx} text={seg.text} />;
        if (seg.type === 'added') return <Added key={idx} text={seg.text} />;
        return <span key={idx} className="text-fg-body">{seg.text}</span>;
      })}
    </div>
  );
};

export default InlineDiff;
